import { useEffect, useRef, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Image as ImageIcon,
  Loader2,
  Printer,
  X,
  Check,
  FileWarning,
} from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatDate, formatFileSize } from '@/lib/format'
import { documentDownloadName, previewKind } from '@/lib/projectDocuments'
import { printImage, printPdfFrame, saveObjectUrl } from '@/lib/printBlob'
import { showErrorToast } from '@/lib/toast'
import type { ProjectDocumentDto } from '@/lib/api/v2/projects'

interface DocumentViewerDialogProps {
  documents: ProjectDocumentDto[]
  /** Index in `documents` of the open document; null = closed. */
  index: number | null
  onIndexChange: (index: number | null) => void
  getUrl: (documentId: string) => Promise<string>
  seenIds: ReadonlySet<string>
  onSeen: (documentId: string) => void
}

type LoadState = { status: 'loading' } | { status: 'ready'; url: string } | { status: 'error' }
type KeyedLoad = { documentId: string; state: LoadState }

/**
 * SOLY-DOC-002 — full-screen viewer over the documents in the order the table shows them:
 * ←/→ (or the side arrows / the strip) move between files, "12 / 30" says where you are, and each
 * file opened is ticked "vu" for this session. PDFs use the browser's own viewer, photos an <img>.
 */
export function DocumentViewerDialog({ documents, index, onIndexChange, getUrl, seenIds, onSeen }: DocumentViewerDialogProps) {
  const open = index !== null && index >= 0 && index < documents.length
  const doc = open ? documents[index] : null
  const kind = doc ? previewKind(doc.contentType) : 'unsupported'
  const [keyedLoad, setKeyedLoad] = useState<KeyedLoad | null>(null)
  // A result for another document (the previous one) reads as "loading" until the new one lands.
  const load: LoadState = keyedLoad && doc && keyedLoad.documentId === doc.id ? keyedLoad.state : { status: 'loading' }
  const frameRef = useRef<HTMLIFrameElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const stripRef = useRef<HTMLDivElement>(null)

  const total = documents.length
  const go = (delta: number) => {
    if (index === null || total === 0) return
    const next = index + delta
    if (next >= 0 && next < total) onIndexChange(next)
  }

  // Load the current document, prefetch the next one.
  useEffect(() => {
    if (!doc) return
    let cancelled = false
    const documentId = doc.id
    getUrl(documentId)
      .then((url) => {
        if (cancelled) return
        setKeyedLoad({ documentId, state: { status: 'ready', url } })
        onSeen(documentId)
      })
      .catch(() => {
        if (!cancelled) setKeyedLoad({ documentId, state: { status: 'error' } })
      })
    const next = index !== null ? documents[index + 1] : undefined
    if (next && previewKind(next.contentType) !== 'unsupported') getUrl(next.id).catch(() => undefined)
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the shown document changes
  }, [doc?.id])

  // Keyboard navigation (the PDF iframe keeps its own keys while it has focus).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return // Alt+← is the browser's "back"
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        go(1)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        go(-1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  useEffect(() => {
    if (index === null) return
    stripRef.current
      ?.querySelector<HTMLElement>(`[data-index="${index}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
  }, [index])

  const download = async () => {
    if (!doc) return
    try {
      saveObjectUrl(await getUrl(doc.id), documentDownloadName(doc))
    } catch {
      showErrorToast('Impossible de télécharger le document')
    }
  }

  const print = () => {
    if (!doc || load.status !== 'ready') return
    if (kind === 'pdf') {
      if (!printPdfFrame(frameRef.current)) showErrorToast("Utilisez l'icône d'impression du lecteur PDF")
    } else if (kind === 'image') {
      printImage(load.url, doc.displayName)
    }
  }

  const seenCount = documents.filter((d) => seenIds.has(d.id)).length

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onIndexChange(null)}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[94vh] w-[96vw] max-w-[96vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[96vw]"
        data-testid="project-document-viewer"
        // Focus lands on "Fermer", inside the dialog: keyboard users never tab through the page behind,
        // and the arrow keys stay ours (the default would focus the first button, "Imprimer").
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          closeRef.current?.focus()
        }}
      >
        {doc && (
          <>
            <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {doc.contentType.startsWith('image/') ? (
                  <ImageIcon className="h-4 w-4 shrink-0 text-slate-500" />
                ) : (
                  <FileText className="h-4 w-4 shrink-0 text-red-500" />
                )}
                <div className="min-w-0">
                  <DialogTitle className="truncate text-sm font-semibold" data-testid="project-document-viewer-title">
                    {doc.displayName}
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    {formatFileSize(doc.size)} · ajouté le {formatDate(doc.createdAt, 'd MMM yyyy')}
                  </DialogDescription>
                </div>
              </div>

              <div className="text-sm tabular-nums" aria-live="polite">
                <span className="font-semibold" data-testid="project-document-viewer-counter">
                  {(index ?? 0) + 1} / {total}
                </span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {seenCount} vu{seenCount > 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={print}
                  disabled={load.status !== 'ready' || kind === 'unsupported'}
                  data-testid="project-document-viewer-print-btn"
                >
                  <Printer className="h-4 w-4" />
                  Imprimer
                </Button>
                <Button variant="outline" size="sm" onClick={download} data-testid="project-document-viewer-download-btn">
                  <Download className="h-4 w-4" />
                  Télécharger
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onIndexChange(null)}
                  ref={closeRef}
                  aria-label="Fermer (Échap)"
                  data-testid="project-document-viewer-close-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 bg-slate-100">
              {load.status === 'loading' && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement du document…
                </div>
              )}
              {(load.status === 'error' || (load.status === 'ready' && kind === 'unsupported')) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                  <FileWarning className="h-8 w-8 text-slate-400" />
                  <p className="text-sm text-muted-foreground" data-testid="project-document-viewer-unavailable">
                    {load.status === 'error'
                      ? "Impossible d'afficher ce document."
                      : 'Aperçu indisponible pour ce format (HEIC, TIFF…).'}
                  </p>
                  <Button size="sm" variant="outline" onClick={download}>
                    <Download className="h-4 w-4" />
                    Télécharger
                  </Button>
                </div>
              )}
              {load.status === 'ready' && kind === 'pdf' && (
                <iframe
                  ref={frameRef}
                  key={doc.id}
                  src={load.url}
                  title={doc.displayName}
                  className="h-full w-full border-0 bg-white"
                  data-testid="project-document-viewer-pdf"
                />
              )}
              {load.status === 'ready' && kind === 'image' && (
                <img
                  key={doc.id}
                  src={load.url}
                  alt={doc.displayName}
                  className="h-full w-full object-contain p-4"
                  data-testid="project-document-viewer-image"
                />
              )}

              <Button
                variant="secondary"
                size="icon"
                className="absolute left-3 top-1/2 h-11 w-11 -translate-y-1/2 rounded-full shadow-md disabled:opacity-0"
                onClick={() => go(-1)}
                disabled={(index ?? 0) <= 0}
                aria-label="Document précédent (←)"
                data-testid="project-document-viewer-prev-btn"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-3 top-1/2 h-11 w-11 -translate-y-1/2 rounded-full shadow-md disabled:opacity-0"
                onClick={() => go(1)}
                disabled={(index ?? 0) >= total - 1}
                aria-label="Document suivant (→)"
                data-testid="project-document-viewer-next-btn"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            <div ref={stripRef} className="flex gap-1.5 overflow-x-auto border-t bg-background px-3 py-2" role="list">
              {documents.map((d, i) => {
                const current = i === index
                const seen = seenIds.has(d.id)
                return (
                  <div key={d.id} role="listitem" className="shrink-0">
                    <button
                      type="button"
                      data-index={i}
                      onClick={() => onIndexChange(i)}
                      aria-current={current ? 'true' : undefined}
                      title={d.displayName}
                      className={cn(
                        'flex max-w-[11rem] items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors',
                        current ? 'border-primary bg-primary/10 font-medium text-foreground' : 'text-muted-foreground hover:bg-muted'
                      )}
                      data-testid="project-document-viewer-strip-item"
                    >
                      <span className="tabular-nums">{i + 1}</span>
                      {d.contentType.startsWith('image/') ? (
                        <ImageIcon className="h-3.5 w-3.5 shrink-0" />
                      ) : (
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                      )}
                      <span className="truncate">{d.displayName}</span>
                      {seen && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-label="vu" />}
                    </button>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
